/**
 * Analytics Event Tracking Utilities
 * Fires to the data layer (GTM / gtag when present) and PostHog in parallel.
 * Events are only fired after the user grants analytics consent via the cookie banner.
 */
import { hasAnalyticsConsent } from './consent';

/**
 * Track a custom event to GTM (gtag or dataLayer) + PostHog
 * @param {string} name - The event name (e.g., 'contact_click', 'generate_lead')
 * @param {Object} params - Additional event parameters
 */
export const trackEvent = (name, params = {}) => {
  if (typeof window === 'undefined' || !hasAnalyticsConsent()) return;
  if (typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  } else if (window.dataLayer && Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: name, ...params });
  }
  if (window.posthog) window.posthog.capture(name, params);
}

/**
 * Fires a page_view on every Gatsby client-side route change (see
 * gatsby-browser.js's onRouteUpdate). Gatsby is an SPA — after the first
 * load, navigating never reloads the page, so nothing else tells GTM/GA4 a
 * new page was seen. Pushed straight to dataLayer rather than through
 * trackEvent: PostHog already autocaptures SPA pageviews via its own
 * history patch (capture_pageview: true in CookieConsentProvider.js), so
 * routing this through trackEvent too would double-report it there.
 */
export const trackPageView = (pagePath) => {
  if (typeof window === 'undefined' || !hasAnalyticsConsent()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'page_view', page_path: pagePath, page_location: window.location.href });
}

/**
 * Track contact method clicks (phone, email)
 * @param {string} method - The contact method used ('phone' or 'email')
 * @param {string} component - The component where the click occurred (e.g., 'footer', 'header', 'contact-form')
 */
export const trackContactClick = (method, component = 'unknown') => {
  trackEvent('contact_click', {
    contact_method: method,
    component_location: component,
  })
}

/**
 * Track lead generation events (form submissions)
 * @param {string} formName - The name of the form that was submitted
 */
export const trackGenerateLead = (formName) => {
  trackEvent('generate_lead', {
    form_name: formName,
  })
}

/**
 * Track CTA button clicks
 * @param {string} ctaText - The text content of the CTA button
 * @param {string} ctaLocation - The location/section where the CTA appears
 */
export const trackCtaClick = (ctaText, ctaLocation = 'unknown') => {
  trackEvent('cta_click', {
    cta_text: ctaText,
    cta_location: ctaLocation,
  })
}

// --- Project Scoper funnel events ---

export const trackScorerStarted = () => {
  trackEvent('scoper_started')
}

export const trackScoperStepCompleted = (step) => {
  trackEvent('scoper_step_completed', { step })
}

export const trackScoperAbandoned = (lastStep) => {
  trackEvent('scoper_abandoned', { last_step: lastStep })
}

export const trackScoperSubmitted = () => {
  trackEvent('scoper_submitted')
}

// --- Pricing Estimator events ---

export const trackPricingCalculated = (params = {}) => {
  trackEvent('pricing_calculated', params)
}

// --- Conversion Enhancement events ---

export const trackExitIntentShown = () => {
  trackEvent('exit_intent_shown')
}

export const trackExitIntentDownload = () => {
  trackEvent('exit_intent_download')
}

export const trackExitIntentEmailSubmitted = () => {
  trackEvent('exit_intent_email_submitted')
}

export const trackExitIntentDismissed = () => {
  trackEvent('exit_intent_dismissed')
}

export const trackSocialProofBarShown = () => {
  trackEvent('social_proof_bar_shown')
}

export const trackSocialProofBarClick = (metric) => {
  trackEvent('social_proof_bar_click', { metric })
}

export const trackBlogInlineCtaClick = (variant, location) => {
  trackEvent('blog_inline_cta_click', { variant, location })
}

export const trackTechRadarCategoryClick = (category) => {
  trackEvent('tech_radar_category_click', { category })
}
